export type ContextAlertActivationInput = {
  contextId: string;
  users: string[];
  attributes: string[];
  token?: string | null;
};

export type ContextAlertActivationResult = {
  users: string[];
  attributes: string[];
  ruleCount: number;
};


const activateRule = async (
  path: string,
  body: Record<string, unknown>,
  token?: string | null,
) => {
  const response = await fetch(`${process.env.REACT_APP_API_HOST || ""}/api/v1${path}`, {
    method: "POST",
    headers: {
      accept: "application/json",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(detail || `No se pudo activar la regla (${response.status}).`);
  }
};

export const activateContextAlerts = async ({
  contextId,
  users: rawUsers,
  attributes: rawAttributes,
  token,
}: ContextAlertActivationInput): Promise<ContextAlertActivationResult> => {
  const users = Array.from(new Set(rawUsers.map((value) => value.trim().toLowerCase()).filter(Boolean)));
  const attributes = Array.from(new Set(rawAttributes.map((value) => value.trim().toLowerCase()).filter(Boolean)));

  if (!contextId) throw new Error("Falta la estación.");
  if (users.length === 0) throw new Error("Ingresá al menos un usuario nominado.");
  if (attributes.length === 0) throw new Error("Esta estación no tiene atributos medidos para alertar.");

  const requests = users.flatMap((user) => [
    activateRule(`/rules/staleness/${encodeURIComponent(contextId)}`, { user, enabled: true }, token),
    activateRule(`/rules/freshness/${encodeURIComponent(contextId)}`, { user, enabled: true }, token),
    ...attributes.flatMap((ctxAttribute) => [
      activateRule(`/rules/median/${encodeURIComponent(contextId)}`, {
        user,
        enabled: true,
        ctxAttribute,
      }, token),
      activateRule(`/rules/roc/${encodeURIComponent(contextId)}`, {
        user,
        enabled: true,
        ctxAttribute,
      }, token),
      activateRule(`/rules/stuck/${encodeURIComponent(contextId)}`, {
        user,
        enabled: true,
        ctxAttribute,
      }, token),
    ]),
  ]);

  await Promise.all(requests);
  return { users, attributes, ruleCount: requests.length };
};

export type ContextAlertDeactivationInput = {
  contextId: string;
  user: string;
  token?: string | null;
};

export const deactivateContextAlerts = async ({
  contextId,
  user: rawUser,
  token,
}: ContextAlertDeactivationInput): Promise<void> => {
  const user = rawUser.trim().toLowerCase();
  if (!contextId) throw new Error("Falta la estación.");
  if (!user) throw new Error("Falta el usuario del seguimiento.");

  await Promise.all([
    activateRule(`/rules/staleness/${encodeURIComponent(contextId)}`, { user, enabled: false }, token),
    activateRule(`/rules/freshness/${encodeURIComponent(contextId)}`, { user, enabled: false }, token),
  ]);
};

export type ThresholdOperator = "gte" | "lte";

export type ThresholdReference = {
  kind: "min" | "max" | "p05" | "p95" | "custom";
  windowDays: number;
  calculatedAt: string;
  value?: number;
  measuredAt?: string | null;
  occurrenceCount?: number;
};

export type ContextThresholdRule = {
  user: string;
  threshold: number;
  ctxAttribute: string;
  ruleTagName: string;
  operator: ThresholdOperator;
  enabled: boolean;
  notifyOnRecovery: boolean;
  reference?: ThresholdReference | null;
  contextName: string;
};

type ThresholdRuleInput = {
  contextId: string;
  user: string;
  attribute: string;
  threshold: number;
  operator: ThresholdOperator;
  notifyOnRecovery: boolean;
  reference: ThresholdReference;
  token?: string | null;
};

const thresholdRuleTag = (attribute: string, _operator: ThresholdOperator) =>
  `${attribute}Umbral`;

export const getContextThresholdRules = async ({
  contextId,
  user: rawUser,
  token,
}: {
  contextId: string;
  user: string;
  token?: string | null;
}): Promise<ContextThresholdRule[]> => {
  const user = rawUser.trim().toLowerCase();
  if (!contextId || !user) return [];
  const response = await fetch(
    `${process.env.REACT_APP_API_HOST || ""}/api/v1/rules/threshold/${encodeURIComponent(contextId)}?user=${encodeURIComponent(user)}`,
    {
      headers: {
        accept: "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    },
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(detail || `No se pudieron cargar las alertas (${response.status}).`);
  }
  return response.json() as Promise<ContextThresholdRule[]>;
};

export const saveContextThresholdRule = async ({
  contextId,
  user: rawUser,
  attribute,
  threshold,
  operator,
  notifyOnRecovery,
  reference,
  token,
}: ThresholdRuleInput): Promise<ContextThresholdRule> => {
  const user = rawUser.trim().toLowerCase();
  if (!contextId || !user || !attribute) throw new Error("Faltan datos de la alerta.");
  if (!Number.isFinite(threshold)) throw new Error("El umbral debe ser un número válido.");

  const response = await fetch(
    `${process.env.REACT_APP_API_HOST || ""}/api/v1/rules/threshold/${encodeURIComponent(contextId)}`,
    {
      method: "POST",
      headers: {
        accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        user,
        threshold,
        ctxAttribute: attribute,
        ruleTagName: thresholdRuleTag(attribute, operator),
        operator,
        enabled: true,
        notifyOnRecovery,
        reference,
      }),
    },
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(detail || `No se pudo guardar la alerta (${response.status}).`);
  }
  return response.json() as Promise<ContextThresholdRule>;
};
